import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import imgUrl from "../../../images/chocolate.webp"

const TITLE = "حاسبة الشوكولاتة - احسب السعرات الحرارية والمكونات الغذائية";
const DESCRIPTION = "حاسبة الشوكولاتة الدقيقة: احسب كمية السعرات الحرارية، السكر، والدهون في أنواع الشوكولاتة المختلفة (الداكنة، البيضاء، وبالحليب) بناءً على الوزن بالجرام.";
const KEYWORDS = "حاسبة الشوكولاتة, سعرات الشوكولاتة, حساب السكر في الشوكولاتة, chocolate calculator, السعرات الحرارية في الكاكاو, فوائد الشوكولاتة الداكنة";
const OG_URL = "https://do-calculate.com/calculator/ar/chocolate-calculator/";

const SCHEMA = [
    {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "حاسبة الشوكولاتة",
        description: DESCRIPTION,
        url: OG_URL,
        applicationCategory: "HealthApplication",
        operatingSystem: "Any",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
        },
    },
    {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "الرئيسية",
                item: "https://do-calculate.com/",
            },
            {
                "@type": "ListItem",
                position: 2,
                name: "الحاسبات",
                item: "https://do-calculate.com/ar/all-calculators/",
            },
            {
                "@type": "ListItem",
                position: 3,
                name: "حاسبة الشوكولاتة",
                item: OG_URL,
            },
        ],
    },
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
            {
                "@type": "Question",
                name: "كم سعرة حرارية في 100 جرام من الشوكولاتة الداكنة؟",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "تحتوي الشوكولاتة الداكنة (70-85% كاكاو) وسطياً على حوالي 598 سعرة حرارية لكل 100 جرام، وتتميز بكونها غنية بالألياف والمعادن مقارنة بالأنواع الأخرى.",
                },
            },
            {
                "@type": "Question",
                name: "هل الشوكولاتة البيضاء تحتوي على كاكاو؟",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "الشوكولاتة البيضاء تحتوي على زبدة الكاكاو ولكنها لا تحتوي على جوامد الكاكاو (التي تعطي اللون البني)، لذا فهي تفتقر إلى مضادات الأكسدة الموجودة في الشوكولاتة الداكنة.",
                },
            },
        ],
    },
];

const CHOCOLATE_DATA = {
    dark: { name: "شوكولاتة داكنة (70-85%)", calories: 5.98, fat: 0.43, sugar: 0.24, protein: 0.08 },
    milk: { name: "شوكولاتة بالحليب", calories: 5.35, fat: 0.30, sugar: 0.51, protein: 0.08 },
    white: { name: "شوكولاتة بيضاء", calories: 5.39, fat: 0.32, sugar: 0.59, protein: 0.06 },
    cocoa_powder: { name: "مسحوق الكاكاو (غير محلى)", calories: 2.28, fat: 0.14, sugar: 0.02, protein: 0.20 },
};

function ChocolateCalculator() {
    const [type, setType] = useState("dark");
    const [weight, setWeight] = useState("");
    const [unit, setUnit] = useState("g");
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const calculateResult = () => {
        setError("");
        const w = parseFloat(weight);
        if (!w || w <= 0) {
            setResult(null);
            return;
        }

        const weightInGrams = unit === "oz" ? w * 28.3495 : w;
        const data = CHOCOLATE_DATA[type];

        setResult({
            calories: weightInGrams * data.calories,
            fat: weightInGrams * data.fat,
            sugar: weightInGrams * data.sugar,
            protein: weightInGrams * data.protein,
            weightG: weightInGrams,
            typeName: data.name,
        });
    };

    useEffect(() => {
        calculateResult();
    }, [type, weight, unit]);

    const handleClear = () => {
        setType("dark");
        setWeight("");
        setUnit("g");
        setResult(null);
        setError("");
    };

    const renderInputFields = () => (
        <div className="row">
            <div className="col-sm-12 col-md-6">
                <label >نوع الشوكولاتة</label>
                <select
                    className="form-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                >
                    {Object.entries(CHOCOLATE_DATA).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                    ))}
                </select>
            </div>
            <div className="col-sm-12 col-md-4">
                <label >الوزن / الكمية</label>
                <input
                    type="number"
                    className="form-control"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="أدخل الكمية"
                />
            </div>
            <div className="col-sm-12 col-md-2">
                <label >الوحدة</label>
                <select
                    className="form-select"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                >
                    <option value="g">جرام (g)</option>
                    <option value="oz">أونصة (oz)</option>
                </select>
            </div>
        </div>
    );

    return (
        <div className="rtl" style={{ direction: "rtl" }}>
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                SCHEMA={SCHEMA}
                IMAGE={imgUrl}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <FirstAds />
                </div>
                <div className="col-12 col-md-4">
                    <img src={imgUrl} alt="حاسبة الشوكولاتة - احسب السعرات الحرارية والمكونات الغذائية"
                        width="350px"
                        height="300px"

                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <br />
                    <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
                        <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة الشوكولاتة والقيم الغذائية</h1>
                    </div>
                    <div className="border border-light mt-3 p-3 bg-white shadow-sm">
                        <div className="mb-4">
                            <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                                تعتبر <strong>حاسبة الشوكولاتة</strong> أداة مثالية لعشاق الحلويات والمهتمين باللياقة البدنية، حيث تتيح لك معرفة <strong>السعرات الحرارية</strong> ومحتوى السكر والدهون بدقة في حصتك اليومية. سواء كنت تفضل الشوكولاتة الداكنة لفوائدها الصحية أو الشوكولاتة بالحليب لمذاقها الغني، تساعدك هذه الحاسبة على موازنة نظامك الغذائي دون حرمان.
                            </p>
                        </div>
                        <hr />
                        <MobileUnderCalAds />
                        <div className="calculatorStyle mb-4">
                            <div className="p-3 mb-3 bg-success text-white text-center rounded">
                                <h2 style={{ fontSize: "18px", margin: 0 }}>بيانات حصة الشوكولاتة</h2>
                            </div>
                            {renderInputFields()}
                            <div className="text-center mb-3 mt-4">
                                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>
                                    مسح البيانات
                                </button>
                            </div>
                            {result && (
                                <div className="mt-4">
                                    <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                                        <strong>النتائج الغذائية لـ {result.weightG.toFixed(1)} جرام:</strong>
                                    </h3>
                                    <div className="mb-3">
                                        <table className="table bmi-table" style={{ width: "100%" }}>
                                            <thead>
                                                <tr>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>العنصر الغذائي</th>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الطاقة (السعرات الحرارية)</td>
                                                    <td style={{ textAlign: "center", fontWeight: "bold", color: "#1a5f3f" }}>{result.calories.toFixed(1)} kcal</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>إجمالي الدهون</td>
                                                    <td style={{ textAlign: "center" }}>{result.fat.toFixed(2)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>السكر</td>
                                                    <td style={{ textAlign: "center" }}>{result.sugar.toFixed(2)} g</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>البروتين</td>
                                                    <td style={{ textAlign: "center" }}>{result.protein.toFixed(2)} g</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="info-sections-container">
                                        <div className="info-section">
                                            <div className="info-section-header"><strong>تفاصيل الحساب</strong></div>
                                            <div className="info-section-body">
                                                <p>بناءً على <strong>{result.typeName}</strong>:</p>
                                                <p>السعرات = الوزن × معامل الطاقة</p>
                                                <p>الدهون = الوزن × نسبة الدهون القياسية</p>
                                                <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                                                    <strong>💡 معلومة:</strong> الشوكولاتة الداكنة تحتوي على سعرات أعلى أحياناً بسبب تركيز زبدة الكاكاو، لكن سكرها أقل بكثير.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <hr />
                        <div className="row">
                            <RelatedLinks />
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-4">
                    <SecondAds />
                    <br />
                    <MainCalculatorsList />
                </div>
            </div>

            <div className="row" style={{ marginTop: "20px" }}>
                <div className="col-12 col-md-8">
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header"><strong>ما هي حاسبة الشوكولاتة؟</strong></div>
                            <div className="info-section-body">
                                <p>حاسبة الشوكولاتة هي أداة رقمية صُممت لتقدير المحتوى الغذائي لمنتجات الكاكاو المختلفة. لاستخدامها، ما عليك سوى تحديد نوع الشوكولاتة التي تتناولها، ثم إدخال الوزن بالجرام أو الأونصة. تقوم الحاسبة فوراً بتحليل البيانات وتقديم تقرير مفصل حول السعرات الحرارية والدهون والسكريات، مما يساعدك في اتخاذ قرارات صحية مستنيرة أثناء التسوق أو تحضير الحلويات.</p>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header"><strong>استخدامات الحاسبة في حياتك اليومية</strong></div>
                            <div className="info-section-body">
                                <ul>
                                    <li><strong>تتبع الحمية الغذائية:</strong> مراقبة السعرات الحرارية بدقة لتجنب زيادة الوزن.</li>
                                    <li><strong>خبز الحلويات:</strong> حساب القيم الغذائية للكيك والبراونيز عند استخدام كميات محددة من الشوكولاتة.</li>
                                    <li><strong>لمرضى السكري:</strong> تقدير كمية السكر الموجودة في الحصة الواحدة لاختيار الأنواع الأنسب.</li>
                                    <li><strong>تحسين الأداء الرياضي:</strong> معرفة الطاقة المستمدة من الشوكولاتة الداكنة قبل التمارين المكثفة.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header"><strong>حقائق غذائية عن أنواع الشوكولاتة</strong></div>
                            <div className="info-section-body">
                                <table className="table bmi-table">
                                    <thead>
                                        <tr>
                                            <th>النوع</th>
                                            <th>السعرات (لكل 100 جم)</th>
                                            <th>الميزة الأساسية</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>داكنة (85%)</td>
                                            <td>598</td>
                                            <td>غنية بمضادات الأكسدة</td>
                                        </tr>
                                        <tr>
                                            <td>بالحليب</td>
                                            <td>535</td>
                                            <td>طعم كريمي وسكر أعلى</td>
                                        </tr>
                                        <tr>
                                            <td>بيضاء</td>
                                            <td>539</td>
                                            <td>خالية من جوامد الكاكاو</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header"><strong>أمثلة عملية</strong></div>
                            <div className="info-section-body">
                                <p>1. <strong>قطعة صغيرة:</strong> إذا تناولت مكعباً واحداً من الشوكولاتة الداكنة يزن 10 جرام، ستحصل على حوالي 60 سعرة حرارية.</p>
                                <p>2. <strong>لوح كامل:</strong> لوح شوكولاتة بالحليب يزن 50 جرام يحتوي على حوالي 267 سعرة حرارية وحوالي 25 جرام من السكر.</p>
                                <p>3. <strong>كاكاو الطبخ:</strong> ملعقتان كبيرتان (15 جرام) من مسحوق الكاكاو الخام تحتويان على 34 سعرة فقط ولكن ببروتين عالٍ.</p>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header"><strong>نصائح لتناول الشوكولاتة بذكاء</strong></div>
                            <div className="info-section-body">
                                <p>احرص دائماً على اختيار الأنواع التي تحتوي على 70% كاكاو أو أكثر للحصول على الفوائد الصحية للقلب. تجنب "بدائل الشوكولاتة" التي تستخدم الزيوت المهدرجة بدلاً من زبدة الكاكاو الطبيعية، فهي ترفع الكوليسترول الضار. تذكر أن الاعتدال هو السر؛ فمكعبان يومياً كافيان تماماً لإشباع رغبتك دون التأثير على رشاقتك.</p>
                            </div>
                        </div>

                        <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>أسئلة متكررة</h2>
                        <div className="info-section">
                            <div className="info-section-header"><h3>هل الشوكولاتة الداكنة تزيد الوزن؟</h3></div>
                            <div className="info-section-body">
                                تزيد الشوكولاتة الداكنة الوزن فقط إذا تم تناولها بكميات كبيرة تتجاوز احتياجك اليومي من السعرات. رغم فوائدها، إلا أنها كثيفة الطاقة، لذا يجب تناولها باعتدال (حوالي 20-30 جرام يومياً).
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>ما الفرق بين الكاكاو الخام والشوكولاتة؟</h3></div>
                            <div className="info-section-body">
                                الكاكاو الخام هو المسحوق المستخلص من بذور الكاكاو بعد إزالة الزبدة، وهو خالٍ من السكر المضاف. أما الشوكولاتة فهي مزيج من الكاكاو، زبدة الكاكاو، السكر، وأحياناً الحليب.
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>لماذا الشوكولاتة البيضاء ليست "شوكولاتة حقيقية"؟</h3></div>
                            <div className="info-section-body">
                                فنياً، هي لا تحتوي على جوامد الكاكاو الصلبة التي تمنح الشوكولاتة لونها وفوائدها الصحية. هي عبارة عن مزيج من زبدة الكاكاو والسكر والحليب فقط.
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>كم كمية الكافيين في الشوكولاتة؟</h3></div>
                            <div className="info-section-body">
                                كلما زادت قتامة الشوكولاتة، زاد الكافيين. الـ 28 جرام من الشوكولاتة الداكنة تحتوي على حوالي 12-20 ملجم من الكافيين، وهو أقل بكثير من فنجان القهوة (95 ملجم).
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>هل تساعد الشوكولاتة في تحسين المزاج؟</h3></div>
                            <div className="info-section-body">
                                نعم، تحتوي الشوكولاتة على مركبات تحفز إفراز "الإندورفين" و"السيروتونين" في الدماغ، وهي هرمونات مسؤولة عن الشعور بالسعادة والاسترخاء.
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>كيف أحول الأونصة إلى جرام؟</h3></div>
                            <div className="info-section-body">
                                الأونصة الواحدة تساوي تقريباً 28.35 جرام. الحاسبة تقوم بهذا التحويل تلقائياً عند اختيار وحدة "الأونصة" من القائمة المنسدلة.
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>ما هي أفضل طريقة لتخزين الشوكولاتة؟</h3></div>
                            <div className="info-section-body">
                                يُفضل تخزينها في مكان بارد وجاف (15-18 درجة مئوية) بعيداً عن الروائح القوية. الثلاجة قد تسبب "تزهير السكر" وهو الطبقة البيضاء التي تظهر على السطح نتيجة الرطوبة.
                            </div>
                        </div>
                        <div className="info-section">
                            <div className="info-section-header"><h3>هل تحتوي الشوكولاتة على بروتين؟</h3></div>
                            <div className="info-section-body">
                                نعم، تحتوي على كميات صغيرة من البروتين تتراوح بين 6 إلى 9 جرامات لكل 100 جرام، وتأتي هذه الكمية من بذور الكاكاو نفسها ومن الحليب المضاف في الأنواع الأخرى.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChocolateCalculator;